import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useBundleUrlFilterSync } from '../../hooks';

const messages = defineMessages({
  label: {
    id: 'appUpdates.bundles.filters.reset.label',
    defaultMessage: 'Reset',
    description: 'Label for the bundled changes reset filters button',
  },
});

const hasValue = (value: unknown[] | string | null): boolean =>
  value !== null && value.length > 0;

export const BundleResetButton: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useBundleUrlFilterSync();

  const { draftFilters, committedFilters } = state.bundles;

  const hasDraftFilters =
    hasValue(draftFilters.selectedProducts) ||
    hasValue(draftFilters.selectedBundleDate) ||
    hasValue(draftFilters.searchQuery);

  const hasCommittedFilters =
    hasValue(committedFilters.selectedProducts) ||
    hasValue(committedFilters.selectedBundleDate) ||
    hasValue(committedFilters.searchQuery);

  const handleClick = React.useCallback(() => {
    if (hasCommittedFilters) {
      syncFiltersToUrl({
        selectedProducts: null,
        selectedBundleDate: null,
        searchQuery: null,
        cursor: undefined,
      });
      dispatch({ type: 'BUNDLES_FILTERS_RESET' });
    } else {
      dispatch({ type: 'BUNDLES_DRAFT_FILTERS_RESET' });
    }
  }, [dispatch, syncFiltersToUrl, hasCommittedFilters]);

  if (!hasDraftFilters && !hasCommittedFilters) {
    return null;
  }

  return (
    <Button
      testId="app-updates-bundle-reset-filter-button"
      onClick={handleClick}
      appearance="subtle"
    >
      {formatMessage(messages.label)}
    </Button>
  );
};
